import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { resolveVisibleSections, SIDEBAR_SECTIONS } from "@/lib/sections";
import { Search, ArrowRight, Pin, PinOff, X } from "lucide-react";

// Sidebar key → route mapping. Sections'ta yeni item eklendiğinde buraya da eklenmeli.
const SECTION_TO_ROUTE = {
  "panel": "/",
  "personnel": "/personnel",
  "timesheet": "/timesheet",
  "advance-requests": "/advance-requests",
  "personnel-requests": "/personnel-requests",
  "support": "/support",
  "expense-categories": "/expense-categories",
  "expense-analytics": "/expense-analytics",
  "product-types": "/product-types",
  "stock-catalog": "/stock-catalog",
  "report-new": "/report/new",
  "import": "/import",
  "balance": "/balance",
  "daily-summary": "/daily-summary",
  "stock-count": "/stock-count",
  "branch-performance": "/branch-performance",
  "consumption": "/consumption",
  "reports": "/reports",
  "branches": "/branches",
  "users": "/users",
  "merkez-report": "/merkez-report",
  "merkez-expense-categories": "/merkez-expense-categories",
  "joker-takip": "/joker-takip",
  "menuboard": "/menuboard",
  "online-menus": "/online-menus",
  "recipes": "/recipes",
  "storage-usage": "/storage-usage",
  "notification-settings": "/notification-settings",
  "announcements": "/announcements",
  "support-settings": "/support-settings",
  "audit-log": "/audit-log",
  "online-panel": "/online-panel",
  "head-office": "/head-office",
  "merkez-daily": "/merkez-daily",
  "merkez-account-payments": "/merkez-account-payments",
  "rent-tracking": "/rent-tracking",
  "meal-card-payments": "/meal-card-payments",
  "ys-reconcile": "/ys-reconcile",
  "trendyol": "/trendyol",
  "trendyol-store-map": "/trendyol/store-map",
  "trendyol-external-branches": "/trendyol/external-branches",
  "trendyol-live": "/trendyol/live",
  "trendyol-reconcile": "/trendyol/reconcile",
  "trendyol-analytics": "/trendyol/analytics",
  "trendyol-products": "/trendyol/products",
  "trendyol-mutabakat": "/trendyol/mutabakat",
  "trendyol-anomalies": "/trendyol/anomalies",
  "whatsapp": "/whatsapp",
  "utility-bills": "/utility-bills",
  "migros-mutabakat": "/migros/mutabakat",
  "platform-analytics": "/platform",
  "platform-uploads": "/platform/uploads",
  "platform-matching": "/platform/matching",
};

// Türkçe-uyumlu diakritik-duyarsız match
function matchTr(haystack, needle) {
  const norm = (s) => (s || "")
    .toLocaleLowerCase("tr")
    .replace(/ı/g, "i").replace(/ğ/g, "g").replace(/ü/g, "u")
    .replace(/ş/g, "s").replace(/ö/g, "o").replace(/ç/g, "c");
  return norm(haystack).includes(norm(needle));
}

const PIN_KEY = "kh_cmdk_pins";

export default function CommandPalette() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [pins, setPins] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PIN_KEY) || "[]"); } catch { return []; }
  });
  const inputRef = useRef(null);

  // Global keyboard shortcut: Cmd/Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      const isMac = /Mac|iPhone|iPod|iPad/.test(navigator.platform);
      if ((isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setSelectedIdx(0);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  const visibleSet = useMemo(() => resolveVisibleSections(user), [user]);

  // Kullanıcıya görünen ve route eşleşmesi olan öğeleri hazırla
  const items = useMemo(() => {
    const arr = [];
    for (const sec of SIDEBAR_SECTIONS) {
      if (!visibleSet.has(sec.key)) continue;
      const route = SECTION_TO_ROUTE[sec.key];
      if (!route) continue;
      arr.push({ key: sec.key, label: sec.label, route, group: sec.group });
    }
    return arr;
  }, [visibleSet]);

  // Filtrelenmiş sonuçlar — sabitlenmişler en üstte
  const results = useMemo(() => {
    const q = query.trim();
    const filtered = q ? items.filter((it) => matchTr(it.label, q) || matchTr(it.key, q)) : items;
    const pinnedItems = pins.map((k) => filtered.find((it) => it.key === k)).filter(Boolean);
    const rest = filtered.filter((it) => !pins.includes(it.key));
    return [...pinnedItems, ...rest];
  }, [items, query, pins]);

  useEffect(() => { setSelectedIdx(0); }, [query]);

  const togglePin = (key) => {
    const nx = pins.includes(key) ? pins.filter((k) => k !== key) : [...pins, key];
    setPins(nx);
    localStorage.setItem(PIN_KEY, JSON.stringify(nx));
  };

  const go = (it) => {
    if (!it) return;
    setOpen(false);
    navigate(it.route);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[selectedIdx]);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 backdrop-blur-sm px-4 pt-[15vh]"
      onClick={() => setOpen(false)}
      data-testid="command-palette-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
        data-testid="command-palette"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Sayfa ara — örn. bilanço, puantaj, trendyol..."
            className="flex-1 outline-none text-sm bg-transparent placeholder:text-slate-400"
            data-testid="command-palette-input"
          />
          <kbd className="hidden md:inline-flex text-[10px] font-mono text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">ESC</kbd>
          <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700 md:hidden">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[50vh] overflow-y-auto">
          {results.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-slate-400">
              Eşleşme bulunamadı
            </div>
          )}
          {results.map((it, i) => {
            const isSelected = i === selectedIdx;
            const isPinned = pins.includes(it.key);
            return (
              <div
                key={it.key}
                onMouseEnter={() => setSelectedIdx(i)}
                onClick={() => go(it)}
                className={`flex items-center gap-2 px-4 py-2.5 cursor-pointer text-sm ${isSelected ? "bg-[#F5EDE4]" : "hover:bg-slate-50"}`}
                data-testid={`command-palette-item-${it.key}`}
              >
                {isPinned && <Pin className="h-3 w-3 text-[#D95D39]" />}
                <span className={`flex-1 ${isSelected ? "text-slate-900 font-medium" : "text-slate-700"}`}>{it.label}</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-400">{it.group}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); togglePin(it.key); }}
                  className="text-slate-300 hover:text-[#D95D39]"
                  title={isPinned ? "Sabitlemeyi kaldır" : "Sabitle"}
                  data-testid={`command-palette-pin-${it.key}`}
                >
                  {isPinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                </button>
                <ArrowRight className={`h-3.5 w-3.5 ${isSelected ? "text-[#D95D39]" : "text-slate-300"}`} />
              </div>
            );
          })}
        </div>
        <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 flex items-center gap-3 text-[11px] text-slate-500">
          <span><kbd className="font-mono border border-slate-200 rounded px-1">↑↓</kbd> gez</span>
          <span><kbd className="font-mono border border-slate-200 rounded px-1">↵</kbd> git</span>
          <span><kbd className="font-mono border border-slate-200 rounded px-1">Pin</kbd> sabitle</span>
          <span className="ml-auto">{results.length} sonuç</span>
        </div>
      </div>
    </div>
  );
}
